/**
 * Enhanced error handling utilities
 * Provides centralized error handling with proper typing and error tracking
 */

/**
 * Custom application error class
 * Extends Error with additional context for better error handling
 */
export class AppError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
    public context?: Record<string, unknown>
  ) {
    super(message);
    this.name = "AppError";

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
}

/**
 * Error codes for different error types
 */
export enum ErrorCode {
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  NETWORK_ERROR = "NETWORK_ERROR",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  AUTHENTICATION_ERROR = "AUTHENTICATION_ERROR",
  AUTHORIZATION_ERROR = "AUTHORIZATION_ERROR",
  NOT_FOUND = "NOT_FOUND",
  RATE_LIMIT_ERROR = "RATE_LIMIT_ERROR",
  SERVER_ERROR = "SERVER_ERROR",
  DATABASE_ERROR = "DATABASE_ERROR",
}

/**
 * Handles errors with proper typing and context
 * @param error - The error to handle (unknown type for safety)
 * @param context - Context where the error occurred
 * @param additionalContext - Additional context data
 * @returns AppError instance
 */
export function handleError(
  error: unknown,
  context: string,
  additionalContext?: Record<string, unknown>
): AppError {
  // If it's already an AppError, return it
  if (error instanceof AppError) {
    return error;
  }

  // Handle Error instances
  if (error instanceof Error) {
    // Check for network errors
    if (error.message.includes("fetch") || error.message.includes("network")) {
      return new AppError(
        "Network error occurred. Please check your connection.",
        ErrorCode.NETWORK_ERROR,
        503,
        { originalError: error.message, context, ...additionalContext }
      );
    }

    // Check for validation errors
    if (error.message.includes("validation") || error.message.includes("invalid")) {
      return new AppError(error.message, ErrorCode.VALIDATION_ERROR, 400, {
        context,
        ...additionalContext,
      });
    }

    // Generic error
    return new AppError(
      error.message || "An unexpected error occurred",
      ErrorCode.UNKNOWN_ERROR,
      500,
      { originalError: error.message, context, ...additionalContext }
    );
  }

  // Handle string errors
  if (typeof error === "string") {
    return new AppError(error, ErrorCode.UNKNOWN_ERROR, 500, { context, ...additionalContext });
  }

  // Handle unknown error types
  return new AppError("An unknown error occurred", ErrorCode.UNKNOWN_ERROR, 500, {
    error,
    context,
    ...additionalContext,
  });
}

/**
 * Safely extracts error message from unknown error type
 * @param error - Error to extract message from
 * @returns Error message string
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof AppError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return "An unknown error occurred";
}

/**
 * Safely extracts error code from unknown error type
 * @param error - Error to extract code from
 * @returns Error code string
 */
export function getErrorCode(error: unknown): string {
  if (error instanceof AppError) {
    return error.code;
  }

  return ErrorCode.UNKNOWN_ERROR;
}

/**
 * Checks if error is a specific type
 * @param error - Error to check
 * @param code - Error code to check against
 * @returns true if error matches code
 */
export function isErrorCode(error: unknown, code: ErrorCode): boolean {
  return getErrorCode(error) === code;
}

/**
 * Logs error to console with context (can be extended to send to error tracking service)
 * @param error - Error to log
 * @param context - Context where error occurred
 */
export function logError(error: unknown, context: string): void {
  const appError = handleError(error, context);

  console.error(`[${context}]`, {
    message: appError.message,
    code: appError.code,
    statusCode: appError.statusCode,
    context: appError.context,
    stack: appError.stack,
  });

  // Integrate with Sentry error tracking (async to avoid blocking)
  import("@/services/sentry")
    .then(({ captureException }) => {
      captureException(appError, {
        contexts: { custom: appError.context },
        tags: { errorCode: appError.code },
      });
    })
    .catch(() => {
      // Silently fail if Sentry is not available (e.g., in development or if DSN not configured)
      // This prevents Sentry errors from breaking the app
    });
}
